import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, XIcon } from '@heroicons/react/solid';
import guideSlides from '../../config/guideData';

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  exit: (direction) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
    transition: { duration: 0.3, ease: 'easeIn' },
  }),
};

export default function GuideSlider({ onClose }) {
  const slides = guideSlides;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [direction, setDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isPreloading, setIsPreloading] = useState(true);

  const progressRef = useRef(0);
  const startTimeRef = useRef(null);
  const animFrameRef = useRef(null);
  const currentSlideRef = useRef(0);
  const hasStartedRef = useRef(false);
  const isPausedRef = useRef(false);
  const pauseOffsetRef = useRef(0);
  const pauseStartRef = useRef(null);
  const hideTimerRef = useRef(null);
  const audioRef = useRef(null);
  const controlsRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 820);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1000);
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [isMobile]);

  const clearAnim = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    hasStartedRef.current = hasStarted;
  }, [hasStarted]);

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // تحميل كل ملفات الصوت من البداية
  useEffect(() => {
    const audioFiles = slides.filter(s => s.audio).map(s => s.audio);
    if (audioFiles.length === 0) { setIsPreloading(false); return; }
    let loaded = 0;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; setIsPreloading(false); }, 5000);
    const preloadAudio = (url) => {
      const a = new Audio();
      a.preload = 'auto';
      a.addEventListener('canplaythrough', () => {
        if (timedOut) return;
        loaded++;
        if (loaded >= audioFiles.length) { clearTimeout(timeout); setIsPreloading(false); }
      }, { once: true });
      a.addEventListener('error', () => {
        if (timedOut) return;
        loaded++;
        if (loaded >= audioFiles.length) { clearTimeout(timeout); setIsPreloading(false); }
      }, { once: true });
      a.src = url;
      a.load();
    };
    audioFiles.forEach(preloadAudio);
  }, []);

  const runSlideRef = useRef(null);

  const runSlide = (targetIndex, dir) => {
    clearAnim();
    startTimeRef.current = null;
    progressRef.current = 0;
    setSlideProgress(0);
    setDirection(dir);
    currentSlideRef.current = targetIndex;
    setCurrentSlide(targetIndex);

    // تشغيل الصوت الخاص بالشريحة
    if (audioRef.current) {
      audioRef.current.pause();
      const slide = slides[targetIndex];
      if (slide.audio) {
        const fullUrl = window.location.origin + slide.audio;
        if (audioRef.current.src !== fullUrl) {
          audioRef.current.src = slide.audio;
          audioRef.current.currentTime = 0;
        }
        audioRef.current.muted = isMuted;
        audioRef.current.play().catch(() => {});
      }
    }

    setIsPaused(false);
    isPausedRef.current = false;
    pauseOffsetRef.current = 0;
    pauseStartRef.current = null;

    if (!hasStartedRef.current && targetIndex > 0) {
      setHasStarted(true);
      hasStartedRef.current = true;
    }

    requestAnimationFrame(() => {
      startTimerForSlide(targetIndex);
    });
  };

  runSlideRef.current = runSlide;

  const startTimerForSlide = (slideIndex) => {
    clearAnim();
    const slide = slides[slideIndex];
    if (!slide) return;

    const durationMs = (slide.duration || 5) * 1000;
    startTimeRef.current = Date.now();
    progressRef.current = 0;

    const tick = () => {
      if (!startTimeRef.current) return;
      if (isPausedRef.current) {
        animFrameRef.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = Date.now() - startTimeRef.current - pauseOffsetRef.current;
      const progress = Math.min(elapsed / durationMs, 1);
      progressRef.current = progress;
      setSlideProgress(progress);
      if (progress >= 1) {
        setIsPaused(true);
        isPausedRef.current = true;
        return;
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  };

  const nextSlide = () => {
    if (currentSlideRef.current < slides.length - 1) {
      runSlide(currentSlideRef.current + 1, 1);
    }
  };

  const goToSlide = (index) => {
    if (index < 0 || index >= slides.length) return;
    runSlide(index, index > currentSlideRef.current ? 1 : -1);
  };

  const handleStart = () => {
    setHasStarted(true);
    hasStartedRef.current = true;
    runSlide(0, 1);
  };

  const handleRestart = () => {
    setIsPaused(false);
    isPausedRef.current = false;
    pauseOffsetRef.current = 0;
    pauseStartRef.current = null;
    handleStart();
  };

  const togglePause = () => {
    if (isPausedRef.current) {
      // إذا الشريحة خلصت، نعيد تشغيلها من البداية
      if (progressRef.current >= 1) {
        runSlide(currentSlideRef.current, 1);
        return;
      }
      if (pauseStartRef.current) pauseOffsetRef.current += Date.now() - pauseStartRef.current;
      // استئناف الصوت بس لو الشريحة لسه ما خلصتش
      if (audioRef.current && progressRef.current < 1) {
        const slide = slides[currentSlideRef.current];
        if (slide?.audio) {
          audioRef.current.play().catch(() => {});
        }
      }
    } else {
      pauseStartRef.current = Date.now();
      // إيقاف الصوت
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
    setIsPaused(!isPausedRef.current);
    isPausedRef.current = !isPausedRef.current;
  };

  const handleContainerClick = (e) => {
    if (e.target.closest('button') || e.target.closest('.scrubber-area')) return;
    togglePause();
  };

  const resetHideTimer = () => {
    if (isMobile) return;
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1000);
  };

  const handleContainerMouseMove = () => {
    if (isMobile) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setControlsVisible(true);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1000);
  };

  const handleControlsEnter = () => {
    if (isMobile) return;
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const handleControlsLeave = () => {
    if (isMobile) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1000);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) audioRef.current.muted = !isMuted;
  };

  const displayProgress = Math.min(slideProgress, 0.99);
  const slide = slides[currentSlide] || slides[0];
  const isCover = slide.type === 'cover';
  const isEnd = slide.type === 'end';
  const isLast = currentSlide === slides.length - 1;

  return (
    <div
      className={`relative ${isMobile ? 'w-[400px]' : 'w-[960px]'} select-none flex flex-col justify-between overflow-hidden bg-[#0a0f1d] rounded-2xl`}
      style={{ height: isMobile ? '750px' : '700px' }}
      onClick={handleContainerClick}
      onMouseMove={handleContainerMouseMove}
    >
      {/* شاشة التحميل */}
      {isPreloading && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0f1d]">
          <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-yellow-400 text-sm font-medium">يتم تحميل المحتوى...</p>
        </div>
      )}

      {/* الشريحة الأولى: الخلفية الضبابية الممتدة (تختفي على الموبايل) */}
      {isCover && !isMobile && (
        <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
          <img
            src={slide.image}
            alt=""
            className="w-full h-full object-cover blur-2xl opacity-30 scale-105"
          />
          <div className="absolute inset-0 bg-[#0a0f1d]/60" />
        </div>
      )}

      {/* محتوى الشرايح */}
      <div className="flex-1 flex flex-col md:flex-row w-full items-stretch overflow-hidden relative z-10">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={slide.id + '-content-wrapper'}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full h-full flex flex-col md:flex-row items-stretch"
          >
              {isCover ? (
              isMobile ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-[#fad94a]/20 via-[#0a0f1d] to-[#0a0f1d]" dir="rtl">
                  <div className="max-w-sm">
                    <h2 className="text-[21px] font-bold text-white mb-4 leading-tight">
                      {slide.title}
                    </h2>
                    <p className="text-[16px] text-gray-300 mb-8 leading-relaxed whitespace-pre-line">
                      {slide.text}
                    </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!hasStarted) handleStart();
                          else if (displayProgress >= 0.99) nextSlide();
                          else togglePause();
                        }}
className="relative min-w-[140px] px-6 py-2.5 bg-gradient-to-l from-[#fad94a] to-[#f4bf49] text-black font-bold rounded-lg text-sm shadow-md transition-all overflow-hidden"
                      >
                        {hasStarted && (
                          <span
                            className="absolute inset-0 bg-yellow-200/70"
                            style={{ width: `${displayProgress * 100}%` }}
                          />
                        )}
                        <span className="relative z-10 inline-flex items-center gap-2">
                          {hasStarted && displayProgress >= 0.99 ? (
                            <>التالي<ChevronLeftIcon className="w-7 h-7 text-black" /></>
                          ) : (
                            <>
                              {slide.buttonText || 'ابدأ الآن'}
                              {!hasStarted || isPaused ? (
                                <svg className="w-7 h-7 text-black" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              ) : (
                                <svg className="w-7 h-7 text-black" viewBox="0 0 24 24" fill="currentColor">
                                  <rect x="6" y="4" width="4" height="16" rx="1" />
                                  <rect x="14" y="4" width="4" height="16" rx="1" />
                                </svg>
                              )}
                            </>
                          )}
                        </span>
                      </button>
                  </div>
                </div>
              ) : (
              <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 items-stretch" dir="rtl">
                
                {/* اليمين: كادر الموبايل */}
                <div className="w-full h-full relative overflow-hidden flex items-center justify-center order-1">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    /* هنا التعديل الجوهري عشان الزوم والتحريك يشتغلوا معاك حلاوة */
                    style={{
                      clipPath: 'inset(0% 0% 0% 0% round 24px 24px 0px 0px)',
                      transform: 'scale(1.25) translateY(125px)', /* <--- غير الزوم هنا من 1.40 والتحريك بالبكسل من 36px براحتك */
                    }}
                    className="h-full object-cover object-top transition-transform duration-200"
                  />
                </div>

                {/* الشمال: النص */}
                <div 
                  className="w-full h-full flex flex-col justify-center p-8 md:p-16 items-start text-right order-2"
                >
                  <h2 className="text-[22px] md:text-[22px] font-bold text-white mb-4 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-[14px] md:text-[18px] text-gray-300 mb-8 max-w-sm leading-relaxed whitespace-pre-line">
                    {slide.text}
                  </p>

                  <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!hasStarted) handleStart();
                        else if (displayProgress >= 0.99) nextSlide();
                        else togglePause();
                      }}
                      className="relative min-w-[140px] px-6 py-2.5 bg-gradient-to-l from-[#fad94a] to-[#f4bf49] text-black font-bold rounded-lg text-sm shadow-md transition-all overflow-hidden"
                    >
                      {hasStarted && (
                        <span
                          className="absolute inset-0 bg-yellow-200/70"
                          style={{ width: `${displayProgress * 100}%` }}
                        />
                      )}
                      <span className="relative z-10 inline-flex items-center gap-2">
                        {hasStarted && displayProgress >= 0.99 ? (
                          <>التالي<ChevronLeftIcon className="w-7 h-7 text-black" /></>
                        ) : (
                          <>
                            {slide.buttonText || 'ابدأ الآن'}
                            {!hasStarted || isPaused ? (
                              <svg className="w-7 h-7 text-black" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            ) : (
                              <svg className="w-7 h-7 text-black" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="6" y="4" width="4" height="16" rx="1" />
                                <rect x="14" y="4" width="4" height="16" rx="1" />
                              </svg>
                            )}
                          </>
                        )}
                      </span>
                    </button>
                </div>

              </div>
              )
            ) : isEnd ? (
              <div className="w-full h-full flex flex-col md:flex-row items-stretch">
                <div className="w-full md:w-1/2 flex flex-col justify-center items-center text-center p-6">
                  <h2 className="text-[20px] md:text-[24px] font-bold text-gray-900 dark:text-white mb-4">
                    {slide.title}
                  </h2>
                  <p className="text-[14px] md:text-[16px] text-gray-600 dark:text-gray-300 mb-6 max-w-md leading-relaxed whitespace-pre-line">
                    {slide.text}
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
                    className="px-6 py-3 bg-gradient-to-l from-[#fad94a] to-[#f4bf49] text-black font-bold rounded-full text-sm shadow-lg"
                  >
                    {slide.buttonText || 'تصفح المنصة'}
                  </button>
                </div>
                <div className="w-full md:w-1/2 relative overflow-hidden flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-700">
                  <img src={slide.image} alt={slide.title} className="w-full h-full object-contain p-4" />
                </div>
              </div>
            ) : (
              <div className={`w-full h-full ${isMobile ? 'flex flex-col' : 'grid grid-cols-1 md:grid-cols-2'} items-stretch`} dir="rtl">
                {/* الصورة يمين / كامل الموبايل — ببلور في آخر شريحة */}
                <div className={`relative overflow-hidden flex items-center justify-center ${isMobile ? 'flex-1 mt-12' : 'w-full h-full'} order-1`}>
                    {isMobile ? (
                      <div className="relative" style={{
                        width: slide.mobileImageScale ? `${slide.mobileImageScale * 100}%` : '100%',
                        height: slide.mobileImageScale ? `${slide.mobileImageScale * 100}%` : '100%',
                      }}>
                        <img
                          src={slide.image}
                          alt={slide.title}
                          style={{
                            clipPath: 'inset(0% 0% 0% 0% round 24px)',
                            objectPosition: slide.mobileObjectPosition || '50% 0%',
                            filter: isLast ? 'blur(24px)' : undefined,
                          }}
                          className="h-full w-full object-cover"
                        />
                        {!isLast && (
                          <div
                            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-6 h-6 z-20"
                            style={{
                              top: slide.mobileDotTop || slide.dotTop || '45%',
                              left: slide.mobileDotLeft || slide.dotLeft || '40%',
                            }}
                          >
                            <span className="absolute w-full h-full rounded-full border-2 border-red-600 animate-ping opacity-40" />
                            <span
                              className="absolute w-[60%] h-[60%] rounded-full border-[3px] border-red-600 bg-transparent"
                              style={{
                                animation: 'shrink-grow 1s cubic-bezier(0.25, 1, 0.5, 1) infinite'
                              }}
                            />
                            <style>{`
                              @keyframes shrink-grow {
                                0% { transform: scale(1.2); }
                                15% { transform: scale(0.50); }
                                100% { transform: scale(1.2); }
                              }
                            `}</style>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className="relative h-full flex items-center justify-center transition-transform duration-200"
                        style={{
                          width: 'fit-content',
                          maxWidth: '100%',
                          transform: `scale(${slide.imageScale || 0.85}) translateY(${slide.imageTranslateY || 0}px)`,
                          transformOrigin: 'top center',
                        }}
                      >
                        <img
                          src={slide.image}
                          alt={slide.title}
                          style={{
                            clipPath: 'inset(0% 0% 0% 0% round 24px)',
                          }}
                          className="h-full object-cover object-top"
                        />
                        {!isLast && (
                          <div
                            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-6 h-6 z-20"
                            style={{
                              top: slide.dotTop || '45%',
                              left: slide.dotLeft || '40%',
                            }}
                          >
                            <span className="absolute w-full h-full rounded-full border-2 border-red-600 animate-ping opacity-40" />
                            <span
                              className="absolute w-[60%] h-[60%] rounded-full border-[3px] border-red-600 bg-transparent"
                              style={{
                                animation: 'shrink-grow 1s cubic-bezier(0.25, 1, 0.5, 1) infinite'
                              }}
                            />
                            <style>{`
                              @keyframes shrink-grow {
                                0% { transform: scale(1.2); }
                                15% { transform: scale(0.50); }
                                100% { transform: scale(1.2); }
                              }
                            `}</style>
                          </div>
                        )}
                      </div>
                    )}
                    {/* إعادة + شكرا على الموبايل — فوق الصورة ببلور */}
                    {isMobile && isLast && (
                      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center p-6">
                        <p className="text-white text-[16px] mb-6 text-center leading-relaxed whitespace-pre-line drop-shadow-lg">
                          {slide.text}
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRestart(); }}
                          className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-l from-[#fad94a] to-[#f4bf49] text-black font-bold rounded-lg text-sm shadow-md"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          إعادة
                        </button>
                      </div>
                    )}
                    {/* ازرار التنقل على الموبايل — يمين ويسار */}
                    {isMobile && !isLast && (
                      <button
                        onClick={(e) => { e.stopPropagation(); goToSlide(currentSlide - 1); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
                        style={{ display: currentSlide > 0 ? 'flex' : 'none' }}
                      >
                        <svg className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      </button>
                    )}
                    {isMobile && !isLast && (
                      <button
                        onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
                      >
                        <svg className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] rotate-180" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      </button>
                    )}
                  </div>
                  {/* الكلام شمال — يختفي على الموبايل */}
                {!isMobile && (
                <div className="w-full h-full flex flex-col justify-center p-8 md:p-16 items-start text-right order-2">
                  <h2 className="text-[20px] md:text-[24px] font-bold text-white mb-4 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-[14px] md:text-[16px] text-gray-300 mb-8 max-w-sm leading-relaxed whitespace-pre-line">
                    {slide.text}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (slide.buttonText === 'إعادة') handleRestart();
                      else nextSlide();
                    }}
                    disabled={isLast && slide.buttonText !== 'إعادة'}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-l from-[#fad94a] to-[#f4bf49] text-black font-bold rounded-lg text-sm shadow-md transition-all disabled:opacity-30"
                  >
                    {slide.buttonText === 'إعادة' ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        إعادة
                      </>
                    ) : (
                      <>التالي<ChevronLeftIcon className="w-7 h-7 text-black" /></>
                    )}
                  </button>
                </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* زر الإغلاق — فوق، دايمًا ظاهر عشان المستخدم يقدر يخرج */}
      <div
          className={`absolute top-4 left-4 z-30 transition-opacity duration-300 ${controlsVisible || isMobile ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
            className="bg-red-500 hover:bg-red-600 text-white w-7 h-7 rounded-md flex items-center justify-center transition-colors"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

      {/* طبقة التحكم: شريط + أزرار */}
      <div className={`${isMobile ? 'relative shrink-0' : 'absolute bottom-0 left-0 right-0'} z-20`} dir="ltr">
        {/* شريط شفاف لا يختفي أبدًا عشان يمسك الماوس — يختفي على الموبايل */}
        {!isMobile && (
        <div
          className={`absolute bottom-0 left-0 right-0 z-10 transition-all duration-300 ${controlsVisible || isMobile ? 'h-full' : 'h-2'}`}
          onMouseEnter={handleControlsEnter}
        />
        )}
        {/* المحتوى الفعلي للتحكم */}
        <div
          ref={controlsRef}
          className={`relative z-20 transition-opacity duration-300 p-4 ${controlsVisible || isMobile ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${isMobile ? 'bg-[#0a0f1d]/90' : ''}`}
          onMouseLeave={handleControlsLeave}
        >
          <div className="flex items-center gap-1">
            {/* أزرار التحكم */}
            <div className="flex items-center gap-1 shrink-0">
              {/* زر تشغيل / إيقاف مؤقت */}
              <button
                onClick={(e) => { e.stopPropagation(); togglePause(); }}
                className="w-9 h-9 rounded-md flex items-center justify-center transition-colors"
              >
                  {!hasStarted || isPaused ? (
                  <svg className="w-6 h-6 text-yellow-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                )}
              </button>

              {/* زر كتم الصوت / تشغيل الصوت */}
              <button
                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                className="w-9 h-9 rounded-md flex items-center justify-center transition-colors"
              >
                {isMuted ? (
                  <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 010 14.14" />
                    <path d="M15.54 8.46a5 5 0 010 7.07" />
                  </svg>
                )}
              </button>
            </div>

            {/* شريط التنقل */}
            <div className="flex-1">
              <div
                className="relative w-full flex items-center gap-[3px] cursor-pointer h-6 px-1"
                onClick={(e) => {
                  e.stopPropagation();
                  const children = Array.from(e.currentTarget.children);
                  for (let i = 0; i < children.length; i++) {
                    const r = children[i].getBoundingClientRect();
                    if (e.clientX >= r.left && e.clientX <= r.right) {
                      goToSlide(i);
                      break;
                    }
                  }
                }}
              >
                {slides.map((s, idx) => {
                  const isCurrent = idx === currentSlide;
                  const isDone = idx < currentSlide;
                  return (
                    <div
                      key={s.id}
                      className={`relative flex items-center transition-all duration-200 ${isCurrent ? 'flex-[12] h-3' : 'flex-[0.5] h-2'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToSlide(idx);
                      }}
                    >
                      <div className="relative w-full h-full rounded-full overflow-hidden bg-gray-300 dark:bg-gray-600">
                        {isDone && (idx !== 0 || currentSlide !== 1) && <div className="absolute inset-0 bg-red-500 rounded-full" />}
                        {idx === 0 && isDone && <div className="absolute inset-0 bg-blue-500 rounded-full" />}
                        {isCurrent && (
                          <div
                            className="absolute inset-0 bg-gradient-to-l from-red-500 to-rose-400 rounded-full"
                            style={{ width: `${displayProgress * 100}%` }}
                          />
                        )}
                      </div>
                      {isCurrent && (
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-neutral-100 rounded-full border-2 border-white"
                          style={{ left: `calc(${displayProgress * 100}% - 7px)` }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* عنصر الصوت المخفي */}
      <audio ref={audioRef} preload="none" />
    </div>
  );
}